import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate, fmtMoney, STAGES, SUB_TYPES } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { FileBarChart2, Trash2, RefreshCw, Briefcase, Building2, Users, Download } from "lucide-react";
import { toast } from "sonner";

const REPORT_TYPE_LABEL = {
  account_summary: "Account Summary",
  deals: "Deals report",
  renewals: "Renewals report",
  accounts: "Accounts report",
};

const REPORT_TYPE_COLOR = {
  account_summary: "bg-blue-100 text-blue-800",
  deals: "bg-emerald-100 text-emerald-800",
  renewals: "bg-amber-100 text-amber-800",
  accounts: "bg-indigo-100 text-indigo-800",
};

const STATUS_LABEL = {
  prospect: "Prospect",
  active: "Active",
  at_risk: "At risk",
  churned: "Churned",
};

const STATUS_PILL = {
  prospect: "bg-slate-100 text-slate-700",
  active: "bg-emerald-100 text-emerald-800",
  at_risk: "bg-amber-100 text-amber-800",
  churned: "bg-red-100 text-red-800",
};

const HEALTH_PILL = {
  green: "bg-emerald-100 text-emerald-800",
  amber: "bg-amber-100 text-amber-800",
  red: "bg-red-100 text-red-800",
};

function downloadCsv(filename, headers, rows) {
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const csv = [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Produce a real .xlsx workbook with one sheet per section (KPIs,
 * breakdowns, main list). Uses SheetJS so Excel, Numbers and Google Sheets
 * all open it natively with typed numeric cells (not stringy CSV).
 *
 * `sheets` is: [{ name, aoa: [[row1cells], [row2cells], ...] }]
 * We truncate sheet names to the Excel 31-char limit and strip illegal
 * characters so the write never fails on a long/odd report title.
 */
async function downloadXlsx(filename, sheets) {
  const XLSX = await import("xlsx");
  const wb = XLSX.utils.book_new();
  const used = new Set();
  for (const s of sheets) {
    if (!s || !s.aoa || !s.aoa.length) continue;
    let name = (s.name || "Sheet").replace(/[\\/\\?\\*\\[\\]:]/g, " ").slice(0, 31) || "Sheet";
    // Excel disallows duplicate sheet names (case-insensitive).
    let unique = name, i = 2;
    while (used.has(unique.toLowerCase())) {
      const suffix = ` (${i++})`;
      unique = name.slice(0, 31 - suffix.length) + suffix;
    }
    used.add(unique.toLowerCase());
    const ws = XLSX.utils.aoa_to_sheet(s.aoa);
    // Basic auto column widths — look at the header row + first 50 rows.
    const sample = s.aoa.slice(0, 51);
    const cols = (sample[0] || []).map((_, c) => {
      const max = sample.reduce((m, r) => Math.max(m, String(r?.[c] ?? "").length), 0);
      return { wch: Math.min(60, Math.max(10, max + 2)) };
    });
    ws["!cols"] = cols;
    XLSX.utils.book_append_sheet(wb, ws, unique);
  }
  XLSX.writeFile(wb, filename);
}

const _safeStub = (s) => String(s || "report").replace(/[^a-z0-9-_]+/gi, "-").toLowerCase();
const _todayStamp = () => new Date().toISOString().slice(0, 10);

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [tab, setTab] = useState("generate");
  const [filterType, setFilterType] = useState("all");
  const [active, setActive] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    const [r, a] = await Promise.all([api.get("/reports"), api.get("/accounts")]);
    setReports(r.data);
    setAccounts(a.data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await api.delete(`/reports/${id}`);
    setActive(null);
    load();
  };

  const list = filterType === "all" ? reports : reports.filter((r) => r.type === filterType);

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700">Reporting</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Reports</h1>
          <p className="text-slate-500 text-sm mt-1">Generate and save reports across accounts, deals and renewals.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent border-b border-slate-200 rounded-none w-full justify-start h-auto p-0 gap-1">
          {[["generate", "Generate"], ["saved", `Saved (${reports.length})`]].map(([v, l]) => (
            <TabsTrigger
              key={v}
              value={v}
              data-testid={`reports-tab-${v}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-700 data-[state=active]:shadow-none px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-900"
            >
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="generate" className="mt-6 space-y-5">
          <AccountSummaryGenerator accounts={accounts} onSaved={() => { setTab("saved"); load(); }} />
          <AccountsReportGenerator onSaved={() => { setTab("saved"); load(); }} />
          <DealsReportGenerator accounts={accounts} onSaved={() => { setTab("saved"); load(); }} />
          <RenewalsReportGenerator onSaved={() => { setTab("saved"); load(); }} />
        </TabsContent>

        <TabsContent value="saved" className="mt-6 space-y-3">
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500 font-mono uppercase tracking-wider">Type</Label>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-52" data-testid="reports-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All report types</SelectItem>
                {Object.entries(REPORT_TYPE_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {active ? (
            <ReportDetail report={active} onClose={() => setActive(null)} onDelete={() => remove(active.id)} nav={nav} />
          ) : list.length === 0 ? (
            <Card className="p-12 text-center border-slate-200">
              <FileBarChart2 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <div className="text-sm text-slate-500">No saved reports yet.</div>
            </Card>
          ) : (
            <div className="space-y-2">
              {list.map((r) => (
                <Card key={r.id} className="border-slate-200 p-4 hover:border-blue-200 cursor-pointer transition-colors" onClick={() => setActive(r)} data-testid={`report-row-${r.id}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${REPORT_TYPE_COLOR[r.type] || "bg-slate-100 text-slate-800"}`}>
                          {REPORT_TYPE_LABEL[r.type] || r.type}
                        </span>
                        <div className="font-medium text-slate-900">{r.name}</div>
                      </div>
                      <div className="text-xs text-slate-500 font-mono mt-1">by {r.generated_by} · {fmtDate(r.created_at)}</div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); remove(r.id); }}>
                      <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Account Summary ---------------- */
function AccountSummaryGenerator({ accounts, onSaved }) {
  const [accountId, setAccountId] = useState("");
  const generate = async () => {
    if (!accountId) return toast.error("Select an account");
    await api.post(`/reports/account-summary/${accountId}`);
    toast.success("Account summary generated");
    onSaved();
  };
  return (
    <Card className="border-slate-200">
      <CardHeader className="border-b border-slate-100">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <Building2 className="h-4 w-4 text-blue-600" /> Account summary
        </CardTitle>
        <div className="text-xs text-slate-500">High-level rollup for a single account.</div>
      </CardHeader>
      <CardContent className="p-5 flex items-center gap-3 flex-wrap">
        <Select value={accountId} onValueChange={setAccountId}>
          <SelectTrigger className="w-72" data-testid="reports-account-select"><SelectValue placeholder="Select account…" /></SelectTrigger>
          <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button data-testid="generate-account-summary-btn" onClick={generate} className="gap-2 bg-blue-600 hover:bg-blue-700">
          Generate
        </Button>
      </CardContent>
    </Card>
  );
}

/* ---------------- Deals report ---------------- */
function DealsReportGenerator({ accounts, onSaved }) {
  const [filters, setFilters] = useState({
    name: "", account_id: "", stage: "", subscription_type: "", owner_name: "", start_date: "", end_date: "",
  });

  const generate = async () => {
    const payload = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) payload[k] = v; });
    await api.post("/reports/deals", payload);
    toast.success("Deals report generated");
    onSaved();
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="border-b border-slate-100">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <Briefcase className="h-4 w-4 text-emerald-600" /> Deals report
        </CardTitle>
        <div className="text-xs text-slate-500">Filter by date range, stage, type, owner or account. Aggregates revenue, GP, MRR and weighted forecast.</div>
      </CardHeader>
      <CardContent className="p-5 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label>Report name</Label>
            <Input data-testid="deals-report-name" placeholder="Q2 pipeline" value={filters.name} onChange={(e) => setFilters({ ...filters, name: e.target.value })} />
          </div>
          <div>
            <Label>Account</Label>
            <Select value={filters.account_id || "all"} onValueChange={(v) => setFilters({ ...filters, account_id: v === "all" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All accounts</SelectItem>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Owner</Label>
            <Input value={filters.owner_name} onChange={(e) => setFilters({ ...filters, owner_name: e.target.value })} placeholder="Any owner" />
          </div>
          <div>
            <Label>Stage</Label>
            <Select value={filters.stage || "all"} onValueChange={(v) => setFilters({ ...filters, stage: v === "all" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All stages</SelectItem>
                {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Subscription type</Label>
            <Select value={filters.subscription_type || "all"} onValueChange={(v) => setFilters({ ...filters, subscription_type: v === "all" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {SUB_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Close from</Label>
              <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} />
            </div>
            <div>
              <Label>Close to</Label>
              <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} />
            </div>
          </div>
        </div>
        <Button data-testid="generate-deals-report-btn" onClick={generate} className="bg-blue-600 hover:bg-blue-700">Generate deals report</Button>
      </CardContent>
    </Card>
  );
}

/* ---------------- Renewals report ---------------- */
function RenewalsReportGenerator({ onSaved }) {
  const [filters, setFilters] = useState({ name: "", days_ahead: 90, bucket: "", owner_name: "" });

  const generate = async () => {
    const payload = { days_ahead: Number(filters.days_ahead) || 90 };
    if (filters.name) payload.name = filters.name;
    if (filters.bucket) payload.bucket = filters.bucket;
    if (filters.owner_name) payload.owner_name = filters.owner_name;
    await api.post("/reports/renewals", payload);
    toast.success("Renewals report generated");
    onSaved();
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="border-b border-slate-100">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-amber-600" /> Renewals report
        </CardTitle>
        <div className="text-xs text-slate-500">All Closed Won deals up for renewal in the chosen window with ARR-at-risk.</div>
      </CardHeader>
      <CardContent className="p-5 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label>Report name</Label>
            <Input data-testid="renewals-report-name" placeholder="Q3 renewal book" value={filters.name} onChange={(e) => setFilters({ ...filters, name: e.target.value })} />
          </div>
          <div>
            <Label>Window (days)</Label>
            <Select value={String(filters.days_ahead)} onValueChange={(v) => setFilters({ ...filters, days_ahead: Number(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="60">60 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
                <SelectItem value="180">6 months</SelectItem>
                <SelectItem value="365">12 months</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Bucket</Label>
            <Select value={filters.bucket || "all"} onValueChange={(v) => setFilters({ ...filters, bucket: v === "all" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All buckets</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="due_30">Due 30d</SelectItem>
                <SelectItem value="due_60">Due 60d</SelectItem>
                <SelectItem value="due_90">Due 90d</SelectItem>
                <SelectItem value="later">Later</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Owner</Label>
            <Input value={filters.owner_name} onChange={(e) => setFilters({ ...filters, owner_name: e.target.value })} placeholder="Any owner" />
          </div>
        </div>
        <Button data-testid="generate-renewals-report-btn" onClick={generate} className="bg-blue-600 hover:bg-blue-700">Generate renewals report</Button>
      </CardContent>
    </Card>
  );
}

/* ---------------- Saved report detail ---------------- */
function ReportDetail({ report, onClose, onDelete, nav }) {
  const p = report.payload || {};
  return (
    <Card className="border-slate-200">
      <CardHeader className="border-b border-slate-100 flex flex-row items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${REPORT_TYPE_COLOR[report.type]}`}>
              {REPORT_TYPE_LABEL[report.type]}
            </span>
            <CardTitle className="text-base font-heading">{report.name}</CardTitle>
          </div>
          <div className="text-xs text-slate-500 font-mono mt-1">by {report.generated_by} · {fmtDate(report.created_at)}</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Back</Button>
          <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={onDelete}>
            <Trash2 className="h-4 w-4 mr-1" /> Delete
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-5 space-y-5">
        {report.type === "account_summary" && <AccountSummaryView p={p} />}
        {report.type === "accounts" && <AccountsReportView p={p} report={report} />}
        {report.type === "deals" && <DealsReportView p={p} nav={nav} report={report} />}
        {report.type === "renewals" && <RenewalsReportView p={p} nav={nav} report={report} />}
      </CardContent>
    </Card>
  );
}

const Stat = ({ label, value, accent }) => (
  <div className="bg-slate-50 rounded-md p-3 border border-slate-100">
    <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">{label}</div>
    <div className={`font-heading text-base font-bold mt-0.5 ${accent || ""}`}>{value}</div>
  </div>
);

function AccountSummaryView({ p }) {
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Deals" value={p.deals_total} />
        <Stat label="Open pipeline" value={fmtMoney(p.open_pipeline)} />
        <Stat label="Won revenue" value={fmtMoney(p.won_revenue)} />
        <Stat label="Open tickets" value={p.open_tickets} />
        <Stat label="Contacts" value={p.contacts} />
        <Stat label="Stax active" value={p.stax_active} />
        <Stat label="No. Users" value={p.account?.num_users || 0} />
        <Stat label="Status" value={p.account?.status || "—"} />
      </div>
    </div>
  );
}

/* ---------------- Accounts report ---------------- */
const STATUS_KEYS = ["prospect", "active", "at_risk", "churned"];

function AccountsReportGenerator({ onSaved }) {
  const [name, setName] = useState("");
  const [q, setQ] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [statuses, setStatuses] = useState({ prospect: false, active: true, at_risk: true, churned: false });

  const toggleStatus = (k) => setStatuses((s) => ({ ...s, [k]: !s[k] }));

  const generate = async () => {
    const picked = STATUS_KEYS.filter((k) => statuses[k]);
    const payload = {};
    if (name) payload.name = name;
    if (q) payload.q = q;
    if (ownerName) payload.owner_name = ownerName;
    if (picked.length > 0 && picked.length < STATUS_KEYS.length) payload.statuses = picked;
    await api.post("/reports/accounts", payload);
    toast.success("Accounts report generated");
    onSaved();
  };

  return (
    <Card className="border-slate-200" data-testid="accounts-report-generator">
      <CardHeader className="border-b border-slate-100">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <Users className="h-4 w-4 text-indigo-600" /> Accounts report
        </CardTitle>
        <div className="text-xs text-slate-500">List every account with its account manager, status, health and revenue. Filter by status and owner. Export to CSV from the saved report.</div>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label>Report name</Label>
            <Input data-testid="accounts-report-name" placeholder="Customer book — Feb 2026" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <Label>Account name contains</Label>
            <Input data-testid="accounts-report-q" placeholder="e.g. Leeds" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div>
            <Label>Owner</Label>
            <Input data-testid="accounts-report-owner" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder="Any owner" />
          </div>
        </div>
        <div>
          <Label className="mb-2 block">Status (leave all ticked for every status)</Label>
          <div className="flex flex-wrap gap-4">
            {STATUS_KEYS.map((k) => (
              <label key={k} className="inline-flex items-center gap-2 text-sm cursor-pointer" data-testid={`accounts-report-status-${k}`}>
                <Checkbox checked={statuses[k]} onCheckedChange={() => toggleStatus(k)} />
                <span>{STATUS_LABEL[k]}</span>
              </label>
            ))}
          </div>
        </div>
        <Button data-testid="generate-accounts-report-btn" onClick={generate} className="bg-blue-600 hover:bg-blue-700">Generate accounts report</Button>
      </CardContent>
    </Card>
  );
}

function AccountsReportView({ p, report }) {
  const nav = useNavigate();
  const exportCsv = () => {
    const headers = [
      "Account", "Owner", "Status", "Health", "Industry",
      "No. Users", "MRR", "ARR", "Live?", "Live through",
    ];
    const rows = (p.accounts || []).map((a) => [
      a.name || "",
      a.owner_name || "",
      STATUS_LABEL[a.status] || a.status || "",
      a.health || "",
      a.industry || "",
      a.num_users ?? 0,
      a.mrr ?? 0,
      a.arr ?? 0,
      a.is_live ? "Yes" : "No",
      a.live_through || "",
    ]);
    const safe = _safeStub(report.name || "accounts-report");
    downloadCsv(`${safe}.csv`, headers, rows);
  };

  const exportXlsx = async () => {
    const stub = _safeStub(report.name || "accounts-report");
    const sheets = [];

    // Sheet 1 — Summary KPIs
    sheets.push({ name: "Summary", aoa: [
      ["Metric", "Value"],
      ["Accounts", p.accounts_total ?? 0],
      ["Live customers", p.live_total ?? 0],
      ["Total ARR", p.total_arr ?? 0],
      ["Total MRR", p.total_mrr ?? 0],
      ["Generated at", (report.generated_at || "").slice(0, 19).replace("T", " ")],
    ]});

    // Sheet 2 — By status breakdown
    if (p.by_status?.length) sheets.push({ name: "By status", aoa: [
      ["Status", "Count", "ARR", "MRR"],
      ...p.by_status.map((r) => [STATUS_LABEL[r.status] || r.status, r.count ?? 0, r.arr ?? 0, r.mrr ?? 0]),
    ]});
    // Sheet 3 — By health
    if (p.by_health?.length) sheets.push({ name: "By health", aoa: [
      ["Health", "Count"],
      ...p.by_health.map((r) => [r.health, r.count ?? 0]),
    ]});
    // Sheet 4 — By industry
    if (p.by_industry?.length) sheets.push({ name: "By industry", aoa: [
      ["Industry", "Count", "ARR"],
      ...p.by_industry.map((r) => [r.industry, r.count ?? 0, r.arr ?? 0]),
    ]});
    // Sheet 5 — All accounts
    sheets.push({ name: "Accounts", aoa: [
      ["Account", "Owner", "Status", "Health", "Industry", "No. Users", "MRR", "ARR", "Live", "Live through"],
      ...(p.accounts || []).map((a) => [
        a.name || "",
        a.owner_name || "",
        STATUS_LABEL[a.status] || a.status || "",
        a.health || "",
        a.industry || "",
        a.num_users ?? 0,
        a.mrr ?? 0,
        a.arr ?? 0,
        a.is_live ? "Yes" : "No",
        a.live_through || "",
      ]),
    ]});

    await downloadXlsx(`${stub}-${_todayStamp()}.xlsx`, sheets);
  };

  return (
    <div className="space-y-5" data-testid="accounts-report-view">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Accounts" value={p.accounts_total} />
        <Stat label="Live customers" value={p.live_total} accent="text-emerald-700" />
        <Stat label="Total ARR" value={fmtMoney(p.total_arr)} accent="text-blue-700" />
        <Stat label="Total MRR" value={fmtMoney(p.total_mrr)} accent="text-blue-700" />
        <div className="flex items-end justify-end gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv} className="gap-2" data-testid="accounts-report-csv-btn">
            <Download className="h-4 w-4" /> CSV
          </Button>
          <Button size="sm" onClick={exportXlsx} className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="accounts-report-xlsx-btn">
            <Download className="h-4 w-4" /> Excel
          </Button>
        </div>
      </div>

      {p.by_status && p.by_status.length > 0 && (
        <div>
          <div className="text-sm font-heading font-semibold mb-2">By status</div>
          <div className="flex flex-wrap gap-2">
            {p.by_status.map((r) => (
              <div key={r.status} className={`px-3 py-1.5 rounded-md text-xs font-medium ${STATUS_PILL[r.status] || "bg-slate-100 text-slate-700"}`}>
                {STATUS_LABEL[r.status] || r.status}: <span className="font-mono ml-1">{r.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {p.by_owner && p.by_owner.length > 0 && (
        <div>
          <div className="text-sm font-heading font-semibold mb-2">By owner / account manager</div>
          <Card className="border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-3 py-2">Owner</th>
                  <th className="px-3 py-2 text-right">Accounts</th>
                  <th className="px-3 py-2 text-right">Live</th>
                  <th className="px-3 py-2 text-right">MRR</th>
                  <th className="px-3 py-2 text-right">ARR</th>
                </tr>
              </thead>
              <tbody>
                {p.by_owner.map((r) => (
                  <tr key={r.owner} className="border-b border-slate-100">
                    <td className="px-3 py-2">{r.owner}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.count}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-700">{r.live}</td>
                    <td className="px-3 py-2 text-right font-mono text-blue-700">{fmtMoney(r.mrr)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(r.arr)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      <div>
        <div className="text-sm font-heading font-semibold mb-2">Accounts ({(p.accounts || []).length})</div>
        <Card className="border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                <th className="px-3 py-2">Account</th>
                <th className="px-3 py-2">Owner</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Health</th>
                <th className="px-3 py-2">Industry</th>
                <th className="px-3 py-2 text-right">Users</th>
                <th className="px-3 py-2 text-right">MRR</th>
                <th className="px-3 py-2 text-right">ARR</th>
                <th className="px-3 py-2">Live</th>
                <th className="px-3 py-2">Live through</th>
              </tr>
            </thead>
            <tbody>
              {(p.accounts || []).map((a) => (
                <tr
                  key={a.id}
                  className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => nav(`/app/accounts/${a.id}`)}
                  data-testid={`accounts-report-row-${a.id}`}
                >
                  <td className="px-3 py-2 font-medium text-slate-900">{a.name}</td>
                  <td className="px-3 py-2 text-slate-600">{a.owner_name || <span className="text-slate-400">—</span>}</td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${STATUS_PILL[a.status] || "bg-slate-100 text-slate-700"}`}>
                      {STATUS_LABEL[a.status] || a.status}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {a.health ? (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${HEALTH_PILL[a.health] || "bg-slate-100 text-slate-700"}`}>
                        {a.health}
                      </span>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{a.industry || <span className="text-slate-400">—</span>}</td>
                  <td className="px-3 py-2 text-right font-mono">{a.num_users || 0}</td>
                  <td className="px-3 py-2 text-right font-mono text-blue-700">{fmtMoney(a.mrr)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtMoney(a.arr)}</td>
                  <td className="px-3 py-2">
                    {a.is_live ? (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-emerald-100 text-emerald-800">Yes</span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-600">No</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">{a.live_through ? fmtDate(a.live_through) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}

function DealsReportView({ p, nav, report }) {
  const exportXlsx = async () => {
    const stub = _safeStub(report.name || "deals-report");
    const sheets = [];
    sheets.push({ name: "Summary", aoa: [
      ["Metric", "Value"],
      ["Deals", p.deals_total ?? 0],
      ["Total revenue", p.total_revenue ?? 0],
      ["Total GP", p.total_gp ?? 0],
      ["Total MRR", p.total_mrr ?? 0],
      ["Generated at", (report.generated_at || "").slice(0, 19).replace("T", " ")],
    ]});
    if (p.by_stage?.length) sheets.push({ name: "By stage", aoa: [
      ["Stage", "Count", "Revenue"],
      ...p.by_stage.map((r) => [r.stage, r.count ?? 0, r.revenue ?? 0]),
    ]});
    if (p.by_subscription_type?.length) sheets.push({ name: "By subscription type", aoa: [
      ["Type", "Count", "Revenue"],
      ...p.by_subscription_type.map((r) => [r.type, r.count ?? 0, r.revenue ?? 0]),
    ]});
    if (p.by_owner?.length) sheets.push({ name: "By owner", aoa: [
      ["Owner", "Count", "Revenue"],
      ...p.by_owner.map((r) => [r.owner, r.count ?? 0, r.revenue ?? 0]),
    ]});
    sheets.push({ name: "Deals", aoa: [
      ["Deal", "Account", "Stage", "Subscription type", "Revenue", "MRR", "GP", "Close date"],
      ...(p.deals || []).map((d) => [
        d.name || "",
        d.account_name || "",
        d.stage || "",
        d.subscription_type || "",
        d.revenue ?? 0,
        d.mrr ?? 0,
        d.gp ?? 0,
        d.close_date || "",
      ]),
    ]});
    await downloadXlsx(`${stub}-${_todayStamp()}.xlsx`, sheets);
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Deals" value={p.deals_total} />
        <Stat label="Total revenue" value={fmtMoney(p.total_revenue)} />
        <Stat label="Total GP" value={fmtMoney(p.total_gp)} accent="text-emerald-700" />
        <Stat label="Total MRR" value={fmtMoney(p.total_mrr)} accent="text-blue-700" />
        <div className="flex items-end justify-end">
          <Button size="sm" onClick={exportXlsx} className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="deals-report-xlsx-btn">
            <Download className="h-4 w-4" /> Excel
          </Button>
        </div>
      </div>

      <BreakdownTable title="By stage" rows={p.by_stage} keyField="stage" />
      <BreakdownTable title="By subscription type" rows={p.by_subscription_type} keyField="type" />
      <BreakdownTable title="By owner" rows={p.by_owner} keyField="owner" />

      {p.deals && p.deals.length > 0 && (
        <div>
          <div className="text-sm font-heading font-semibold mb-2">Deals ({p.deals.length})</div>
          <Card className="border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-3 py-2">Deal</th>
                  <th className="px-3 py-2">Account</th>
                  <th className="px-3 py-2">Stage</th>
                  <th className="px-3 py-2 text-right">Revenue</th>
                  <th className="px-3 py-2 text-right">MRR</th>
                  <th className="px-3 py-2 text-right">GP</th>
                  <th className="px-3 py-2">Close</th>
                </tr>
              </thead>
              <tbody>
                {p.deals.map((d) => (
                  <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2">{d.name}</td>
                    <td className="px-3 py-2 text-slate-600">{d.account_name}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{d.stage}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(d.revenue)}</td>
                    <td className="px-3 py-2 text-right font-mono text-blue-700">{fmtMoney(d.mrr)}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-700">{fmtMoney(d.gp)}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(d.close_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}
    </div>
  );
}

function RenewalsReportView({ p, nav, report }) {
  const exportXlsx = async () => {
    const stub = _safeStub(report.name || "renewals-report");
    const sheets = [];
    sheets.push({ name: "Summary", aoa: [
      ["Metric", "Value"],
      ["Renewals total", p.items_total ?? 0],
      ["ARR at risk", p.arr_at_risk ?? 0],
      ["Total MRR", p.total_mrr ?? 0],
      ...Object.entries(p.summary || {}).map(([k, v]) => [k.replace(/_/g, " "), v ?? 0]),
      ["Generated at", (report.generated_at || "").slice(0, 19).replace("T", " ")],
    ]});
    if (p.by_owner?.length) sheets.push({ name: "By owner", aoa: [
      ["Owner", "Count", "Revenue"],
      ...p.by_owner.map((r) => [r.owner, r.count ?? 0, r.revenue ?? 0]),
    ]});
    if (p.by_account?.length) sheets.push({ name: "By account", aoa: [
      ["Account", "Count", "Revenue"],
      ...p.by_account.map((r) => [r.account, r.count ?? 0, r.revenue ?? 0]),
    ]});
    sheets.push({ name: "Renewals", aoa: [
      ["Account", "Deal", "Bucket", "Stage", "Renews", "Days to renewal", "Revenue", "MRR"],
      ...(p.items || []).map((it) => [
        it.account_name || "",
        it.name || "",
        it.bucket || "",
        it.stage || "",
        it.end_date || it.close_date || "",
        it.days_to_renewal ?? "",
        it.revenue ?? 0,
        it.mrr ?? 0,
      ]),
    ]});
    await downloadXlsx(`${stub}-${_todayStamp()}.xlsx`, sheets);
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Renewals" value={p.items_total} />
        <Stat label="ARR at risk" value={fmtMoney(p.arr_at_risk)} accent="text-red-700" />
        <Stat label="Total MRR" value={fmtMoney(p.total_mrr)} accent="text-blue-700" />
        <Stat label="Overdue" value={p.summary?.overdue || 0} accent="text-red-700" />
        <div className="flex items-end justify-end">
          <Button size="sm" onClick={exportXlsx} className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="renewals-report-xlsx-btn">
            <Download className="h-4 w-4" /> Excel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(p.summary || {}).map(([k, v]) => (
          <Stat key={k} label={k.replace("_", " ")} value={v} />
        ))}
      </div>

      <BreakdownTable title="By owner" rows={p.by_owner} keyField="owner" />
      <BreakdownTable title="By account" rows={p.by_account} keyField="account" />

      {p.items && p.items.length > 0 && (
        <div>
          <div className="text-sm font-heading font-semibold mb-2">Upcoming renewals</div>
          <Card className="border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-3 py-2">Account · Deal</th>
                  <th className="px-3 py-2">Bucket</th>
                  <th className="px-3 py-2">Renews</th>
                  <th className="px-3 py-2 text-right">In</th>
                  <th className="px-3 py-2 text-right">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {p.items.map((it) => (
                  <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => nav(`/app/accounts/${it.account_id}`)}>
                    <td className="px-3 py-2">
                      <div className="text-xs text-slate-500">{it.account_name}</div>
                      <div>{it.name}</div>
                    </td>
                    <td className="px-3 py-2 text-xs"><span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-medium">{it.bucket}</span></td>
                    <td className="px-3 py-2 text-xs text-slate-600">{fmtDate(it.end_date)}</td>
                    <td className="px-3 py-2 text-right font-mono">{it.days_to_renewal}d</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(it.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}
    </div>
  );
}

function BreakdownTable({ title, rows, keyField }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div>
      <div className="text-sm font-heading font-semibold mb-2">{title}</div>
      <Card className="border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
              <th className="px-3 py-2">{keyField.charAt(0).toUpperCase() + keyField.slice(1)}</th>
              <th className="px-3 py-2 text-right">Count</th>
              <th className="px-3 py-2 text-right">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r[keyField]} className="border-b border-slate-100">
                <td className="px-3 py-2">{r[keyField]}</td>
                <td className="px-3 py-2 text-right font-mono">{r.count}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtMoney(r.revenue || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
